import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  postId: string;
}

const REPORT_REASONS = [
  { id: 'spam', label: 'スパム・宣伝' },
  { id: 'harassment', label: 'ハラスメント・嫌がらせ' },
  { id: 'inappropriate', label: '不適切なコンテンツ' },
  { id: 'copyright', label: '著作権侵害' },
  { id: 'other', label: 'その他' },
];

export function ReportModal({ isOpen, onClose, postId }: ReportModalProps) {
  const { user } = useAuth();
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [detail, setDetail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!selectedReason) return;
    setIsSubmitting(true);
    try {
      await supabase.from('reports').insert({
        reporter_id: user?.id ?? null,
        target_type: 'post',
        target_id: postId,
        reason: selectedReason,
        detail: detail.trim() || null,
      });
      setIsSuccess(true);
    } catch (err) {
      console.error('Report failed:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setSelectedReason(null);
    setDetail('');
    setIsSuccess(false);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm"
            onClick={handleClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-50 max-w-sm mx-auto rounded-2xl overflow-hidden"
            style={{
              background: 'rgba(10, 10, 10, 0.98)',
              border: '1px solid rgba(255, 50, 50, 0.25)',
              boxShadow: '0 0 60px rgba(255, 50, 50, 0.1)',
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
              <div className="flex items-center gap-3">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{ background: 'rgba(255, 50, 50, 0.15)', border: '1px solid rgba(255, 50, 50, 0.3)' }}
                >
                  <i className="ri-alarm-warning-fill text-red-400 text-sm" />
                </div>
                <h2 className="text-white font-display text-lg tracking-wider">投稿を通報</h2>
              </div>
              <button
                onClick={handleClose}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-gray-400 text-xl" />
              </button>
            </div>

            {isSuccess ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="px-6 py-10 flex flex-col items-center gap-4 text-center"
              >
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center"
                  style={{ background: 'rgba(0, 255, 65, 0.15)', border: '1px solid rgba(0, 255, 65, 0.3)' }}
                >
                  <i className="ri-check-line text-neon-green text-3xl" />
                </div>
                <p className="text-white font-bold tracking-wider">通報を受け付けました</p>
                <p className="text-gray-500 text-sm leading-relaxed">
                  ご報告ありがとうございます。内容を確認し、適切に対応いたします。
                </p>
                <button
                  onClick={handleClose}
                  className="mt-2 px-8 py-2.5 rounded-full text-sm font-bold tracking-wider text-white hover:bg-white/10 transition-colors cursor-pointer"
                  style={{ border: '1px solid rgba(255,255,255,0.15)' }}
                >
                  閉じる
                </button>
              </motion.div>
            ) : (
              <>
                <div className="px-6 py-5 space-y-4">
                  <p className="text-gray-400 text-sm leading-relaxed">
                    通報する理由を選択してください。
                  </p>
                  <div className="space-y-2">
                    {REPORT_REASONS.map((reason) => (
                      <button
                        key={reason.id}
                        onClick={() => setSelectedReason(reason.id)}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 cursor-pointer text-left"
                        style={{
                          background:
                            selectedReason === reason.id
                              ? 'rgba(255, 50, 50, 0.12)'
                              : 'rgba(255, 255, 255, 0.03)',
                          border:
                            selectedReason === reason.id
                              ? '1px solid rgba(255, 50, 50, 0.4)'
                              : '1px solid rgba(255, 255, 255, 0.06)',
                        }}
                      >
                        <div
                          className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center"
                          style={{
                            border:
                              selectedReason === reason.id
                                ? '2px solid #ff3232'
                                : '2px solid rgba(255,255,255,0.2)',
                            background:
                              selectedReason === reason.id ? '#ff3232' : 'transparent',
                          }}
                        >
                          {selectedReason === reason.id && (
                            <div className="w-1.5 h-1.5 rounded-full bg-white" />
                          )}
                        </div>
                        <span
                          className="text-sm tracking-wide"
                          style={{ color: selectedReason === reason.id ? '#fff' : '#888' }}
                        >
                          {reason.label}
                        </span>
                      </button>
                    ))}
                  </div>

                  {selectedReason === 'other' && (
                    <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}>
                      <textarea
                        value={detail}
                        onChange={(e) => setDetail(e.target.value)}
                        rows={3}
                        maxLength={300}
                        placeholder="詳細を入力してください..."
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-red-500/40 transition-colors resize-none"
                      />
                    </motion.div>
                  )}
                </div>

                <div className="px-6 py-4 border-t border-white/5 flex items-center gap-3">
                  <button
                    onClick={handleClose}
                    className="flex-1 py-3 rounded-xl text-gray-400 text-sm font-bold tracking-wider hover:bg-white/5 transition-colors cursor-pointer"
                    style={{ border: '1px solid rgba(255, 255, 255, 0.08)' }}
                  >
                    キャンセル
                  </button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleSubmit}
                    disabled={!selectedReason || isSubmitting}
                    className="flex-1 py-3 rounded-xl text-white text-sm font-bold tracking-wider transition-all duration-200 cursor-pointer"
                    style={{
                      background:
                        !selectedReason || isSubmitting
                          ? 'rgba(255, 50, 50, 0.2)'
                          : 'linear-gradient(135deg, #ff3232, #cc0000)',
                      boxShadow:
                        !selectedReason || isSubmitting ? 'none' : '0 0 20px rgba(255,50,50,0.3)',
                    }}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin" />
                        送信中...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-alarm-warning-fill" />
                        通報する
                      </span>
                    )}
                  </motion.button>
                </div>
              </>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
